import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Cpu, Plus, UploadCloud } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, type AiModelRelease } from "../api";
import { EmptyState, ErrorNotice, LoadingRows, Status, formatTime } from "../components";

const checkpointLimit = 64 * 1024 * 1024;

export function AiModelsPage({ role }: { role: string }) {
  const client = useQueryClient();
  const [editorOpen, setEditorOpen] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const models = useQuery({ queryKey: ["ai-models"], queryFn: () => api<AiModelRelease[]>("/admin/v1/ai-models"), refetchInterval: 15000 });
  const refresh = () => client.invalidateQueries({ queryKey: ["ai-models"] });
  const create = useMutation({
    mutationFn: (body: unknown) => api<{ id: string; status: string }>("/admin/v1/ai-models", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: async () => {
      setEditorOpen(false);
      setFeedback("模型文件已上传。AI 节点校验并预热后，可在列表中发布。");
      await refresh();
    },
  });
  const publish = useMutation({
    mutationFn: (item: AiModelRelease) => api<{ active_model_release_id: string }>(`/admin/v1/ai-models/${item.id}/publish`, {
      method: "POST", body: JSON.stringify({ reason: `控制台发布 AI 模型 ${item.version}` }),
    }),
    onSuccess: async (_, item) => {
      setFeedback(`${item.version} 已成为新建 AI 任务的默认模型；已有任务仍使用原版本。`);
      await refresh();
    },
  });
  const prewarm = useMutation({
    mutationFn: (item: AiModelRelease) => api<{ candidate_model_release_id: string }>(`/admin/v1/ai-models/${item.id}/prewarm`, {
      method: "POST", body: JSON.stringify({ reason: `控制台预热 AI 模型 ${item.version}` }),
    }),
    onSuccess: async (_, item) => {
      setFeedback(`${item.version} 已加入预热目标；节点就绪后可发布或回滚。`);
      await refresh();
    },
  });
  const upload = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const checkpoint = data.get("checkpoint");
    const config = data.get("config");
    if (!(checkpoint instanceof File) || !(config instanceof File) || checkpoint.size === 0 || checkpoint.size > checkpointLimit || config.size === 0 || config.size > 1024 * 1024) {
      setFeedback("请上传不超过 64 MiB 的 .pt 模型和不超过 1 MiB 的生产配置 YAML。");
      return;
    }
    setFeedback(null);
    const checkpointBase64 = await fileToBase64(checkpoint);
    create.mutate({
      version: String(data.get("version") ?? "").trim(),
      model_id: String(data.get("model_id") ?? "").trim(),
      checkpoint_base64: checkpointBase64,
      config_text: await config.text(),
      reason: String(data.get("reason") ?? "").trim(),
    });
  };
  const active = models.data?.find((model) => model.is_active);
  const error = models.error ?? create.error ?? prewarm.error ?? publish.error;
  return <div className="page page--ai-models">
    <header className="page-heading"><div><p className="eyebrow">NODES / AI MODEL</p><h1>AI 模型</h1><p>上传 Strong V2 兼容模型，等待节点校验与预热后发布。发布只影响新建的 ai-v1 任务。</p></div>
      {role === "ADMIN" && <button className="button button--primary" type="button" onClick={() => setEditorOpen((value) => !value)}><Plus size={17} />上传模型</button>}
    </header>
    {error && <ErrorNotice error={error} />}
    {feedback && <div className="notice notice--neutral" role="status"><Check size={17} />{feedback}</div>}
    <section className="ai-model-current" aria-label="当前 AI 模型">
      <div><p className="eyebrow">当前生效</p><strong>{active?.version ?? "尚未发布"}</strong><span>{active ? `${active.model_id} · ${active.ready_nodes} 个 READY 节点` : "上传并预热模型后发布，AI 任务才可创建"}</span></div>
      <Cpu size={28} aria-hidden="true" />
    </section>
    {editorOpen && role === "ADMIN" && <form className="ai-model-upload" onSubmit={(event) => void upload(event)}>
      <div className="section-heading"><div><p className="eyebrow">NEW RELEASE</p><h2>上传兼容模型</h2></div><button type="button" className="button button--quiet" onClick={() => setEditorOpen(false)}>收起 <ChevronDown size={16} /></button></div>
      <p>仅接受当前 Strong V2 rc12 运行时 ABI。上传后不会立即切换线上模型。</p>
      <div className="ai-model-upload__grid">
        <label className="field">版本标识<input name="version" placeholder="例如 strong-v2-20260925" required maxLength={128} pattern="[A-Za-z0-9._-]+" /></label>
        <label className="field">模型 ID<input name="model_id" placeholder="例如 a2f64lc1tp" required maxLength={128} pattern="[A-Za-z0-9._-]+" /></label>
        <label className="field">PyTorch checkpoint (.pt)<input name="checkpoint" type="file" accept=".pt" required /></label>
        <label className="field">生产配置 (.yaml)<input name="config" type="file" accept=".yaml,.yml" required /></label>
        <label className="field field--wide">变更原因<textarea name="reason" required minLength={3} maxLength={500} placeholder="例如：新模型完成验收，准备预热" /></label>
      </div>
      <button type="submit" className="button button--primary" disabled={create.isPending}><UploadCloud size={16} />{create.isPending ? "正在上传…" : "上传并预热"}</button>
    </form>}
    <section className="section-block section-block--first">
      <div className="section-heading"><div><p className="eyebrow">MODEL RELEASES</p><h2>模型版本</h2></div><span className="section-heading__meta">{models.data?.length ?? 0} 个版本</span></div>
      {models.isPending ? <LoadingRows columns={6} /> : models.data?.length ? <div className="table-wrap"><table className="responsive-table ai-model-table"><thead><tr><th>版本</th><th>校验状态</th><th>就绪节点</th><th>任务引用</th><th>创建时间</th><th aria-label="操作" /></tr></thead><tbody>{models.data.map((item) => <tr key={item.id}>
        <td data-label="版本"><strong>{item.version}</strong><small>{item.model_id} · {item.runtime_abi}</small><small>SHA-256 {item.checkpoint_sha256.slice(0, 16)}…</small></td>
        <td data-label="校验状态"><Status value={item.status} />{item.is_active && <small className="ai-model-table__active">当前生效</small>}{item.is_prewarm_target && <small className="ai-model-table__active">预热目标</small>}{item.last_error && <small title={item.last_error}>{item.last_error}</small>}</td>
        <td data-label="就绪节点">{item.ready_nodes}</td>
        <td data-label="任务引用">{item.pending_jobs}</td>
        <td data-label="创建时间">{formatTime(item.created_at)}</td>
        <td className="responsive-table__actions">{role === "ADMIN" && !item.is_active && <div className="ai-model-actions"><button type="button" className="button button--small" disabled={item.is_prewarm_target || !["READY", "VALIDATING"].includes(item.status) || prewarm.isPending} onClick={() => prewarm.mutate(item)}>{prewarm.isPending && prewarm.variables?.id === item.id ? "预热中…" : "预热"}</button><button type="button" className="button button--small" disabled={item.status !== "READY" || item.ready_nodes === 0 || publish.isPending} title={item.status !== "READY" ? "等待节点校验模型" : item.ready_nodes === 0 ? "至少一台 AI 节点就绪后才能发布" : undefined} onClick={() => publish.mutate(item)}>{publish.isPending && publish.variables?.id === item.id ? "发布中…" : "发布 / 回滚"}</button></div>}</td>
      </tr>)}</tbody></table></div> : <EmptyState title="还没有 AI 模型">先配置默认对象存储，再上传 Strong V2 checkpoint 与生产 YAML。</EmptyState>}
    </section>
  </div>;
}

async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let start = 0; start < bytes.length; start += 8192) {
    binary += String.fromCharCode(...bytes.subarray(start, start + 8192));
  }
  return btoa(binary);
}
